import { Reference, useMutation } from '@apollo/client';
import { useState } from 'react';
import {
    SplitTransactionEntryDocument,
    TransactionEntryFieldsFragmentDoc,
} from '../../graphql/generated';
import { sum } from '../../util/arrays';
import { createSplit, TransactionEntrySplit } from './transactionEntrySplit';
import { CashflowTransactionEntry } from './types';

export type OnUpdateFunc = (updates: Partial<TransactionEntrySplit>) => void;

export interface SplitBlockProps {
    split: TransactionEntrySplit;
    index: number;
    onUpdate: OnUpdateFunc;
    onDelete: (index: number) => void;
    leftToSplit: number;
    canDelete: boolean;
    isPositiveTransaction: boolean;
}

export const useSplitFormProps = ({
    transactionEntry,
    onSuccess,
    onError,
    onAddSplit,
}: {
    transactionEntry: CashflowTransactionEntry;
    onSuccess: VoidFunction;
    onError: (err: unknown) => void;
    onAddSplit: VoidFunction;
}) => {
    const [splitTransactionEntry, { loading: isSubmitting }] = useMutation(
        SplitTransactionEntryDocument,
        {
            update(cache, { data }) {
                if (!data?.splitTransactionEntry?.transactionEntries) return;

                data.splitTransactionEntry.transactionEntries.forEach(
                    (transactionEntry) => {
                        cache.modify({
                            fields: {
                                transactionEntries(existingEntries = []) {
                                    const newEntryRef = cache.writeFragment({
                                        data: transactionEntry,
                                        fragment:
                                            TransactionEntryFieldsFragmentDoc,
                                        fragmentName: 'TransactionEntryFields',
                                    });

                                    // Avoid duplicates
                                    if (
                                        newEntryRef &&
                                        existingEntries.some(
                                            (ref: Reference) =>
                                                cache.identify(ref) ===
                                                cache.identify(newEntryRef)
                                        )
                                    ) {
                                        return existingEntries;
                                    }

                                    return [...existingEntries, newEntryRef];
                                },
                            },
                        });
                    }
                );
            },
        }
    );

    const [splits, setSplits] = useState<TransactionEntrySplit[]>([
        createSplit({
            amount: 0,
            categoryId: transactionEntry.category.id,
            merchantId: transactionEntry.merchant?.id,
            isExpanded: true,
        }),
        createSplit({
            amount: 0,
            categoryId: transactionEntry.category.id,
            merchantId: transactionEntry.merchant?.id,
            isExpanded: false,
        }),
    ]);

    const leftToSplit =
        Math.abs(transactionEntry.amount) - sum(splits, (s) => s.amount);

    const handleAddSplit = ({ isExpanded }: { isExpanded: boolean }) => {
        setSplits((currentSplits) => [
            ...currentSplits,
            createSplit({
                amount: leftToSplit,
                categoryId: transactionEntry.category.id,
                isExpanded,
            }),
        ]);

        setTimeout(onAddSplit, 100);
    };

    const handleDeleteSplit = (index: number) => {
        setSplits((currentSplits) =>
            currentSplits.filter((_, i) => i !== index)
        );
    };

    const handleSplitUpdate = (
        updates: Partial<TransactionEntrySplit>,
        index: number
    ) => {
        setSplits((currentSplits) => {
            const newSplits = [...currentSplits];
            // Update the changed split
            newSplits[index] = { ...newSplits[index], ...updates };

            // If we have exactly 2 splits and amount was changed, update the other split
            if (currentSplits.length === 2 && updates.amount) {
                const otherIndex = index === 0 ? 1 : 0;
                const totalAmount = Math.abs(transactionEntry.amount);
                const remainingAmount = totalAmount - updates.amount;

                if (remainingAmount > 0) {
                    newSplits[otherIndex] = {
                        ...newSplits[otherIndex],
                        amount: remainingAmount,
                    };
                }
            }

            return newSplits;
        });
    };

    const disabled = leftToSplit !== 0 || splits.some((s) => s.amount <= 0);

    const handleSplitTransaction = async () => {
        try {
            await splitTransactionEntry({
                variables: {
                    input: {
                        transactionEntryId: transactionEntry.id,
                        splits: splits.map((split) => {
                            // Mobile uses tags, web uses tagNames
                            const tagNames = split.tags.length
                                ? split.tags.map((t) => t.name)
                                : split.tagNames;

                            return {
                                amount: split.amount,
                                categoryId: split.categoryId,
                                merchantId: split.merchantId,
                                tagNames,
                                isHidden: split.isHidden,
                            };
                        }),
                    },
                },
            });

            onSuccess();
        } catch (e) {
            onError(e);
        }
    };

    return {
        splits,
        leftToSplit,
        handleAddSplit,
        handleDeleteSplit,
        handleSplitUpdate,
        disabled,
        handleSplitTransaction,
        isSubmitting,
    };
};
